import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Copy, Check, Download, LogOut, Settings, Upload, X, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { SignaturePreview } from '../components/signature/SignaturePreview';
import ImageCropper from '../components/ImageCropper';
import { formatPhoneNumber, generateVCard, downloadVCard } from '../utils/signature/formatUtils';
import { 
  cleanLinkedInHandle, 
  cleanXHandle, 
  cleanFacebookHandle, 
  cleanInstagramHandle, 
  cleanYouTubeHandle 
} from '../utils/socialMediaUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SignatureTool = () => {
  const { signatureUrl } = useParams();
  const { user, logout } = useAuth();
  const [company, setCompany] = useState(null);
  const [companyStatus, setCompanyStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const signatureRef = useRef(null);
  const isMountedRef = useRef(true);
  
  // Image cropper state
  const [cropperOpen, setCropperOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  
  // Check for preview mode (admin viewing as employee)
  const searchParams = new URLSearchParams(window.location.search);
  const isPreviewMode = searchParams.get('preview') === 'true';

  // Default populated data
  const [signatureData, setSignatureData] = useState({
    fullName: '',
    credentials: '',
    jobTitle: '',
    department: '',
    company: '',
    email: '',
    phoneNumber: '',
    mobileNumber: '',
    faxNumber: '',
    countryCode: '+1',
    website: '',
    address: '',
    addressLink: '',
    bookMeeting: '',
    bookMeetingText: 'Book a Meeting',
    profileImage: '',
    companyLogo: '',
    companyLogoLink: '',
    bannerImage: '',
    bannerLink: '',
    brandColor: '#EB2F5B',
    disclaimer: '',
    linkedin: '',
    xUsername: '',
    facebook: '',
    instagram: '',
    youtube: '',
    useBrandColorForSocial: true,
    signatureSize: 'standard',
    bannerSize: 'medium',
    showFieldLabels: true,
    fieldLabelsScope: 'all',
    showCountryCode: true,
    officeLabel: 'Office',
    mobileLabel: 'Mobile',
    labelStyle: 'brand',
    labelStyleScope: 'phones',
    bannerWidth: null,
  });

  const [fieldEditability, setFieldEditability] = useState({});
  const [fieldVisibility, setFieldVisibility] = useState({
    fullName: true,
    credentials: true,
    jobTitle: true,
    department: false,
    company: false,
    email: true,
    phoneNumber: true,
    mobileNumber: true,
    faxNumber: true,
    website: true,
    address: true,
    addressLink: true,
    bookMeeting: true,
    profileImage: true,
    companyLogo: true,
    bannerImage: true,
    linkedin: true,
    xUsername: true,
    facebook: true,
    instagram: true,
    youtube: true,
    disclaimer: true,
  });
  
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');

  useEffect(() => {
    isMountedRef.current = true;
    
    fetchCompanyInfo().catch(err => {
      console.error('Error in fetchCompanyInfo:', err);
      if (isMountedRef.current) {
        setLoading(false);
      }
    });
    
    return () => {
      isMountedRef.current = false;
    };
  }, [signatureUrl]);

  useEffect(() => {
    if (isMountedRef.current) {
      calculateCharCount();
    }
  }, [signatureData, fieldVisibility]);

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/companies/${signatureUrl}`);
      const companyData = response.data;
      
      if (!companyData) {
        throw new Error('No company data received');
      }
      
      if (!isMountedRef.current) return;
      
      setCompany(companyData);
      
      // Check company status
      const status = companyData.status || 'active';
      setCompanyStatus(status);
      
      // If account is cancelled/disabled, don't load signature data
      if (status === 'cancelled' || status === 'inactive' || status === 'payment_failed') {
        setLoading(false);
        return;
      }
      
      // Merge signature_template and company_config (company_config takes precedence)
      const template = companyData.signature_template || {};
      const config = companyData.company_config || {};
      
      console.log('Company data loaded:', { 
        name: companyData.name, 
        hasConfig: !!config, 
        hasTemplate: !!template 
      });
      
      // Update with company-specific data from both sources
      // Also use placeholder/demo data if admin has set it
      if (isMountedRef.current) {
        // Generate default email using company website domain
        // Extract clean domain from URL (remove protocol, www, etc.)
        const extractDomain = (url) => {
          if (!url) return '';
          // Remove protocol
          let domain = url.replace(/^https?:\/\//, '');
          // Remove www.
          domain = domain.replace(/^www\./, '');
          // Remove trailing slash and path
          domain = domain.split('/')[0];
          return domain;
        };
        
        const companyWebsite = config.website || companyData.website || '';
        const cleanDomain = extractDomain(companyWebsite);
        const defaultEmail = config.default_email || (cleanDomain ? `@${cleanDomain}` : '');
        
        setSignatureData(prev => ({
          ...prev,
          fullName: config.default_full_name || prev.fullName,
          credentials: config.default_credentials || prev.credentials,
          jobTitle: config.default_job_title || prev.jobTitle,
          company: companyData.name,
          email: defaultEmail,
          phoneNumber: config.default_phone_number || prev.phoneNumber,
          mobileNumber: config.default_mobile_number || prev.mobileNumber,
          faxNumber: config.default_fax_number || prev.faxNumber,
          website: config.website || companyData.website || prev.website,
          address: config.default_address || prev.address,
          bookMeeting: config.default_book_meeting || prev.bookMeeting,
          linkedin: config.default_linkedin || prev.linkedin,
          xUsername: config.default_x_username || prev.xUsername,
          facebook: config.default_facebook || prev.facebook,
          instagram: config.default_instagram || prev.instagram,
          youtube: config.default_youtube || prev.youtube,
          brandColor: config.brand_color || companyData.brand_color || prev.brandColor,
          profileImage: config.default_profile_image || prev.profileImage,
          companyLogo: config.company_logo || template.company_logo_url || prev.companyLogo,
          companyLogoLink: config.company_logo_link || template.company_logo_link || prev.companyLogoLink,
          bannerImage: config.banner_image || template.banner_image_url || prev.bannerImage,
          bannerLink: config.banner_link || template.banner_link || prev.bannerLink,
          disclaimer: config.disclaimer || template.disclaimer || prev.disclaimer,
          bookMeetingText: config.meeting_link_text || template.book_meeting_text || prev.bookMeetingText,
          useBrandColorForSocial: config.use_brand_color_for_social_icons ?? template.use_brand_color_for_social ?? prev.useBrandColorForSocial,
          signatureSize: config.signature_size || prev.signatureSize,
          bannerSize: config.banner_size || prev.bannerSize,
          showFieldLabels: config.show_field_labels !== false,
          fieldLabelsScope: config.field_labels_scope || (config.show_field_labels === false ? 'none' : 'all'),
          showCountryCode: config.show_country_code !== false,
          officeLabel: config.office_label ?? 'Office',
          mobileLabel: config.mobile_label ?? 'Mobile',
          labelStyle: config.label_style || 'brand',
          labelStyleScope: config.label_style_scope || 'phones',
          bannerWidth: config.banner_width ?? prev.bannerWidth,
        }));

        setLocations(companyData.locations || []);
      }

      // Build field visibility and editability from config
      const visibility = {};
      const editability = {};
      
      // List of all possible fields
      const allFields = [
        'fullName', 'credentials', 'jobTitle', 'company', 'department', 'countryCode',
        'phoneNumber', 'mobileNumber', 'faxNumber', 'email', 'website', 'address',
        'addressLink', 'location', 'bookMeeting', 'bookMeetingText', 'profileImage',
        'companyLogo', 'companyLogoLink', 'bannerImage', 'bannerLink', 'brandColor',
        'useBrandColorForSocial', 'linkedin', 'xUsername', 'facebook', 'instagram',
        'disclaimer', 'youtube'
      ];
      
      // Check config for visibility and editability settings
      allFields.forEach(fieldName => {
        try {
          // Convert camelCase to snake_case (e.g., fullName -> full_name)
          const snakeCase = fieldName.replace(/([A-Z])/g, '_$1').toLowerCase();
          const visibleKey = `visible_${snakeCase}`;
          const editableKey = `editable_${snakeCase}`;
          
          // Check if explicitly set in config
          if (config[visibleKey] !== undefined) {
            visibility[fieldName] = config[visibleKey];
          } else if (template.fields?.[fieldName]?.visible !== undefined) {
            visibility[fieldName] = template.fields[fieldName].visible;
          } else {
            visibility[fieldName] = true; // Default to visible
          }
          
          if (config[editableKey] !== undefined) {
            editability[fieldName] = config[editableKey];
          } else if (template.fields?.[fieldName]?.editable !== undefined) {
            editability[fieldName] = template.fields[fieldName].editable;
          } else {
            editability[fieldName] = true; // Default to editable
          }
        } catch (err) {
          console.error(`Error processing field ${fieldName}:`, err);
          // Set defaults on error
          visibility[fieldName] = true;
          editability[fieldName] = true;
        }
      });

      if (!isMountedRef.current) return;
      
      console.log('Field Editability:', editability);
      console.log('Field Visibility:', visibility);
      
      setFieldVisibility(visibility);
      setFieldEditability(editability);

    } catch (error) {
      console.error('Error fetching company:', error);
      
      // Handle 403 status (inactive company)
      if (error.response?.status === 403) {
        if (isMountedRef.current) {
          // Show inactive company message
          setCompany({ 
            name: 'Company Inactive', 
            inactive: true,
            message: error.response?.data?.detail || 'This company account is currently inactive.'
          });
        }
        return;
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  // Minify markup only. Whitespace inside text nodes must be preserved or the
  // signature loses real spaces (e.g. "W Loop S, Suite 400" -> "W Loop S,Suite 400").
  const minifyHTML = (html) => {
    return html
      .replace(/<[^>]+>/g, (tag) =>
        tag
          .replace(/\s+/g, ' ')
          .replace(/\s*=\s*/g, '=')
          .replace(/;\s+/g, ';')
          .replace(/:\s+/g, ':')
          .replace(/,\s+/g, ',')
      )
      .replace(/>\s+</g, '><')
      .trim();
  };

  const calculateCharCount = () => {
    if (!signatureRef.current) return;
    
    // Get the HTML content
    let htmlContent = signatureRef.current.innerHTML;
    
    // Convert relative URLs to absolute
    const baseUrl = window.location.origin;
    htmlContent = htmlContent.replace(/src="\/([^"]+)"/g, `src="${baseUrl}/$1"`);
    htmlContent = htmlContent.replace(/href="\/([^"]+)"/g, `href="${baseUrl}/$1"`);
    
    // Minify the HTML
    const minifiedHTML = minifyHTML(htmlContent);
    
    // Count the characters
    setCharCount(minifiedHTML.length);
  };

  const handleDataChange = (key, value) => {
    // Clean social media handles/URLs
    let cleanedValue = value;
    
    switch(key) {
      case 'linkedin':
        cleanedValue = cleanLinkedInHandle(value);
        break;
      case 'xUsername':
        cleanedValue = cleanXHandle(value);
        break;
      case 'facebook':
        cleanedValue = cleanFacebookHandle(value);
        break;
      case 'instagram':
        cleanedValue = cleanInstagramHandle(value);
        break;
      case 'youtube':
        cleanedValue = cleanYouTubeHandle(value);
        break;
      default:
        cleanedValue = value;
    }
    
    setSignatureData(prev => ({ ...prev, [key]: cleanedValue }));
  };

  const handleLocationChange = (locationId) => {
    if (locationId === 'none') {
      // Clear location selection
      setSelectedLocation('');
      // Clear location-related fields when "None" is selected
      setSignatureData(prev => ({
        ...prev,
        address: '',
        addressLink: '',
        phoneNumber: '',
        faxNumber: '',
        countryCode: '+1',
      }));
    } else {
      setSelectedLocation(locationId);
      const location = locations.find(loc => loc.location_id === locationId);
      if (location) {
        setSignatureData(prev => ({
          ...prev,
          address: location.address || '',
          addressLink: location.address_link || '',
          phoneNumber: location.phone_number || '',
          faxNumber: location.fax_number || '',
          countryCode: location.country_code || '+1',
        }));
      }
    }
  };

  const handleImageUpload = async (e, type) => {
    if (!isMountedRef.current) return;
    
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size
    if (file.size > 10 * 1024 * 1024) {
      alert('Image must be less than 10MB');
      return;
    }

    // Read file and open cropper
    const reader = new FileReader();
    reader.onload = () => {
      setImageToCrop({
        src: reader.result,
        type: type,
        aspectRatio: type === 'profile' ? 1 : (type === 'banner' ? 16 / 3 : 1),
        recommendedSize: type === 'profile' 
          ? { width: 200, height: 200 } 
          : type === 'banner'
          ? { width: 1600, height: 600 }
          : { width: 200, height: 100 }, // Logo with flexible height
        allowFreeform: type === 'banner' || type === 'logo' // Allow free-form for banners and logos
      });
      setCropperOpen(true);
    };
    reader.readAsDataURL(file);
    
    // Clear the file input
    e.target.value = '';
  };

  const handleCropComplete = async (croppedBlob, format) => {
    if (!isMountedRef.current || !imageToCrop) return;
    
    try {
      const extension = format === 'image/png' ? 'png' : 'jpg';
      const croppedFile = new File(
        [croppedBlob], 
        `${imageToCrop.type}_${Date.now()}.${extension}`,
        { type: format }
      );
      
      const formData = new FormData();
      formData.append('file', croppedFile);

      const response = await axios.post(`${BACKEND_URL}/api/upload`, formData, {
        headers: { 
          'Accept': 'application/json',
        },
      });

      if (response.data && response.data.success) {
        if (!isMountedRef.current) return;

        // Use absolute URL
        const imageUrl = response.data.url.startsWith('http') 
          ? response.data.url 
          : `${BACKEND_URL}${response.data.url}`;
        
        if (imageToCrop.type === 'profile') {
          handleDataChange('profileImage', imageUrl);
        } else if (imageToCrop.type === 'logo') {
          handleDataChange('companyLogo', imageUrl);
        } else if (imageToCrop.type === 'banner') {
          handleDataChange('bannerImage', imageUrl);
        }
      } else {
        throw new Error('Upload failed - no success response');
      }
    } catch (error) {
      console.error('Upload error:', error);
      if (isMountedRef.current) {
        const errorMsg = error.response?.data?.detail || error.message || 'Failed to upload image';
        alert(`Upload failed: ${errorMsg}`);
      }
    }
  };

  const handleCopy = async () => {
    if (!signatureRef.current || !isMountedRef.current) return;

    try {
      // Get the HTML content
      let htmlContent = signatureRef.current.innerHTML;
      
      // Convert relative image URLs to absolute URLs
      const baseUrl = window.location.origin;
      htmlContent = htmlContent.replace(/src="\/([^"]+)"/g, `src="${baseUrl}/$1"`);
      htmlContent = htmlContent.replace(/href="\/([^"]+)"/g, `href="${baseUrl}/$1"`);
      
      // Minify the HTML to reduce character count
      const minifiedHTML = minifyHTML(htmlContent);
      
      // Create a blob with minified HTML content
      const htmlBlob = new Blob([minifiedHTML], { type: 'text/html' });
      const textBlob = new Blob([signatureRef.current.innerText], { type: 'text/plain' });
      
      // Use Clipboard API for better compatibility
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': htmlBlob,
            'text/plain': textBlob,
          }),
        ]);
      } else {
        // Fallback to old method
        const range = document.createRange();
        range.selectNodeContents(signatureRef.current);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        document.execCommand('copy');
        selection.removeAllRanges();
      }

      if (isMountedRef.current) {
        setCopied(true);
        setTimeout(() => {
          if (isMountedRef.current) setCopied(false);
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
      if (!isMountedRef.current) return;
      // Fallback to simple copy
      const range = document.createRange();
      range.selectNodeContents(signatureRef.current);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      document.execCommand('copy');
      selection.removeAllRanges();
      
      if (isMountedRef.current) {
        setCopied(true);
        setTimeout(() => {
          if (isMountedRef.current) setCopied(false);
        }, 2000);
      }
    }
  };

  const handleDownloadVCard = () => {
    const vCardData = generateVCard({
      ...signatureData,
      company: signatureData.company,
    });
    downloadVCard(vCardData, `${signatureData.fullName.replace(/\s+/g, '_')}.vcf`);
  };

  const isFieldLocked = (field) => {
    // Default to unlocked if field editability not yet loaded
    if (!fieldEditability || typeof fieldEditability !== 'object') return false;
    
    // In preview mode, show as employee view (locked fields)
    if (isPreviewMode) {
      return fieldEditability[field] === false;
    }
    // Admin can always edit (when not in preview mode)
    if (user?.role === 'admin') {
      return false;
    }
    // Check if field is explicitly marked as not editable
    return fieldEditability[field] === false;
  };

  const isFieldVisible = (field) => {
    // Default to visible if field visibility not yet loaded or not specified
    if (!fieldVisibility || typeof fieldVisibility !== 'object') return true;
    return fieldVisibility[field] !== false;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Company Not Found</h2>
          <p className="text-gray-600">The signature tool you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  // Check if company is inactive
  if (company?.inactive) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md mx-4">
          <CardContent className="pt-6 text-center">
            <div className="mb-4">
              <svg className="mx-auto h-16 w-16 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Signature Tool Inactive</h2>
            <p className="text-gray-600 mb-4">
              This company's signature tool is currently inactive.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-medium text-blue-900 mb-1">Need Help?</p>
              <p className="text-sm text-blue-700">
                Please contact customer service to reactivate:
              </p>
              <a 
                href="tel:888-910-1123" 
                className="text-2xl font-bold text-blue-600 hover:text-blue-700 mt-2 block"
              >
                888-910-1123
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check for white label configuration
  const config = company?.company_config || {};
  const isWhiteLabeled = config.white_label_enabled === true;
  
  // Determine logo link - if company has website, use it; otherwise slicksignature.com
  const logoLink = company?.website ? `https://${company.website.replace(/^https?:\/\//, '')}` : 'https://slicksignature.com';
  
  const headerBgColor = config.header_bg_color === 'white' ? 'bg-white' : 'bg-black';
  const headerTextColor = config.header_bg_color === 'white' ? 'text-gray-900' : 'text-white';
  const headerBorderColor = config.header_bg_color === 'white' ? 'border-gray-200' : 'border-gray-800';
  const logoToShow = (isWhiteLabeled && signatureData.companyLogo) 
    ? signatureData.companyLogo 
    : '/slicksignature-logo.png';

  return (
    <div className="min-h-screen bg-white">
      {/* Preview Mode Banner */}
      {isPreviewMode && (
        <div className="bg-yellow-100 border-b-2 border-yellow-400 px-6 py-2">
          <div className="max-w-7xl mx-auto">
            <p className="text-sm text-yellow-800 font-medium">
              👁️ Preview Mode - Viewing as Employee (locked fields are shown)
            </p>
          </div>
        </div>
      )}
      
      {/* Header */}
      <header className={`${headerBgColor} border-b ${headerBorderColor}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <a href={isWhiteLabeled ? (config.website || logoLink) : logoLink} target="_blank" rel="noopener noreferrer">
                <img src={logoToShow} alt={company?.name || 'SlickSignature'} className="h-12" />
              </a>
            </div>
            {user?.role === 'admin' && user?.company_id === company.id && (
              <div className="flex items-center space-x-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className={`${headerTextColor} ${config.header_bg_color === 'white' ? 'border-gray-300 hover:bg-gray-100' : 'border-white hover:bg-gray-800'}`}
                  onClick={() => window.location.href = '/settings'}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className={`${headerTextColor} ${config.header_bg_color === 'white' ? 'border-gray-300 hover:bg-gray-100' : 'border-white hover:bg-gray-800'}`}
                  onClick={() => logout()}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Account Disabled Message */}
        {companyStatus && (companyStatus === 'cancelled' || companyStatus === 'inactive' || companyStatus === 'payment_failed') ? (
          <div className="flex items-center justify-center min-h-[60vh]">
            <Card className="max-w-md w-full">
              <CardContent className="p-8 text-center space-y-6">
                <div className="flex justify-center">
                  <div className="rounded-full bg-red-100 p-4">
                    <Lock className="h-12 w-12 text-red-600" />
                  </div>
                </div>
                <div className="space-y-2">
                  <h2 className="text-2xl font-bold text-gray-900">Account Disabled</h2>
                  <p className="text-gray-600">
                    Please login as admin to update your subscription and reactivate your signature.
                  </p>
                </div>
                <Button 
                  onClick={() => window.location.href = '/login'}
                  className="w-full"
                  size="lg"
                >
                  Login
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Form */}
          <div className="space-y-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6 space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>

                {/* Name and Credentials */}
                <div className="grid grid-cols-2 gap-4">
                  {isFieldVisible('fullName') && (
                    <div className="space-y-2">
                      <Label className="text-sm text-gray-700">Full Name *</Label>
                      <Input
                        value={signatureData.fullName}
                        onChange={(e) => handleDataChange('fullName', e.target.value)}
                        disabled={isFieldLocked('fullName')}
                        className="border-gray-300"
                      />
                    </div>
                  )}
                  
                  {isFieldVisible('credentials') && (
                    <div className="space-y-2">
                      <Label className="text-sm text-gray-700">
                        Credentials {isFieldLocked('credentials') && <Lock className="inline h-3 w-3 text-gray-400" />}
                      </Label>
                      <Input
                        value={signatureData.credentials}
                        onChange={(e) => handleDataChange('credentials', e.target.value)}
                        disabled={isFieldLocked('credentials')}
                        placeholder="MBA, CPA"
                        className="border-gray-300"
                      />
                    </div>
                  )}
                </div>

                {/* Job Title */}
                {isFieldVisible('jobTitle') && (
                  <div className="space-y-2">
                    <Label className="text-sm text-gray-700">Job Title *</Label>
                    <Input
                      value={signatureData.jobTitle}
                      onChange={(e) => handleDataChange('jobTitle', e.target.value)}
                      disabled={isFieldLocked('jobTitle')}
                      className="border-gray-300"
                    />
                  </div>
                )}

                {/* Contact Information */}
                <h3 className="text-lg font-semibold text-gray-900 pt-4">Contact Information</h3>

                {/* Location Dropdown */}
                {locations.length > 0 && isFieldVisible('location') && (
                  <div className="space-y-2">
                    <Label className="text-sm text-gray-700">Office Location</Label>
                    <Select value={selectedLocation || 'none'} onValueChange={handleLocationChange}>
                      <SelectTrigger className="border-gray-300">
                        <SelectValue placeholder="Select a location to auto-fill details" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None (Clear selection)</SelectItem>
                        {locations.map((location) => (
                          <SelectItem key={location.location_id} value={location.location_id}>
                            {location.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Email and Phone */}
                <div className="grid grid-cols-2 gap-4">
                  {isFieldVisible('email') && (
                    <div className="space-y-2">
                      <Label className="text-sm text-gray-700">Email *</Label>
                      <Input
                        type="email"
                        value={signatureData.email}
                        onChange={(e) => handleDataChange('email', e.target.value)}
                        disabled={isFieldLocked('email')}
                        className="border-gray-300"
                      />
                    </div>
                  )}

                  {isFieldVisible('phoneNumber') && (
                    <div className="space-y-2">
                      <Label className="text-sm text-gray-700">
                        Office Phone {isFieldLocked('phoneNumber') && <Lock className="inline h-3 w-3 text-gray-400" />}
                      </Label>
                      <Input
                        value={signatureData.phoneNumber}
                        onChange={(e) => handleDataChange('phoneNumber', formatPhoneNumber(e.target.value))}
                        disabled={isFieldLocked('phoneNumber')}
                        placeholder="(555) 123-4567"
                        className="border-gray-300"
                      />
                    </div>
                  )}
                </div>

                {/* Mobile and Fax */}
                {(isFieldVisible('mobileNumber') || isFieldVisible('faxNumber')) && (
                  <div className="grid grid-cols-2 gap-4">
                    {isFieldVisible('mobileNumber') && (
                      <div className="space-y-2">
                        <Label className="text-sm text-gray-700">Mobile Number</Label>
                        <Input
                          value={signatureData.mobileNumber}
                          onChange={(e) => handleDataChange('mobileNumber', formatPhoneNumber(e.target.value))}
                          disabled={isFieldLocked('mobileNumber')}
                          placeholder="(555) 987-6543"
                          className="border-gray-300"
                        />
                      </div>
                    )}

                    {isFieldVisible('faxNumber') && (
                      <div className="space-y-2">
                        <Label className="text-sm text-gray-700">
                          Fax Number {isFieldLocked('faxNumber') && <Lock className="inline h-3 w-3 text-gray-400" />}
                        </Label>
                        <Input
                          value={signatureData.faxNumber}
                          onChange={(e) => handleDataChange('faxNumber', formatPhoneNumber(e.target.value))}
                          disabled={isFieldLocked('faxNumber')}
                          placeholder="(555) 765-4321"
                          className="border-gray-300"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Address - hidden when admin has configured office locations */}
                {isFieldVisible('address') && locations.length === 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm text-gray-700">
                      Office Address {isFieldLocked('address') && <Lock className="inline h-3 w-3 text-gray-400" />}
                    </Label>
                    <Input
                      value={signatureData.address}
                      onChange={(e) => handleDataChange('address', e.target.value)}
                      disabled={isFieldLocked('address')}
                      placeholder="123 Main St, City, State 12345"
                      className="border-gray-300"
                      data-testid="office-address-input"
                    />
                    {isFieldLocked('address') && (
                      <p className="text-xs text-gray-500">Set by your administrator.</p>
                    )}
                  </div>
                )}

                {/* Meeting Link */}
                {isFieldVisible('bookMeeting') && (
                  <div className="space-y-2">
                    <Label className="text-sm text-gray-700">Meeting Booking URL</Label>
                    <Input
                      value={signatureData.bookMeeting}
                      onChange={(e) => handleDataChange('bookMeeting', e.target.value)}
                      disabled={isFieldLocked('bookMeeting')}
                      placeholder="https://calendly.com/yourname"
                      className="border-gray-300"
                    />
                  </div>
                )}

                {/* Social Media - Only show section if at least one field is visible */}
                {(isFieldVisible('linkedin') || isFieldVisible('xUsername') || isFieldVisible('facebook') || isFieldVisible('instagram') || isFieldVisible('youtube')) && (
                  <>
                    <h3 className="text-lg font-semibold text-gray-900 pt-4">Social Media</h3>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {isFieldVisible('linkedin') && (
                    <div className="space-y-2">
                      <Label className="text-sm text-gray-700">LinkedIn</Label>
                      <Input
                        value={signatureData.linkedin}
                        onChange={(e) => handleDataChange('linkedin', e.target.value)}
                        disabled={isFieldLocked('linkedin')}
                        placeholder="username"
                        className="border-gray-300"
                      />
                    </div>
                  )}

                  {isFieldVisible('xUsername') && (
                    <div className="space-y-2">
                      <Label className="text-sm text-gray-700">X (Twitter)</Label>
                      <Input
                        value={signatureData.xUsername}
                        onChange={(e) => handleDataChange('xUsername', e.target.value)}
                        disabled={isFieldLocked('xUsername')}
                        placeholder="username"
                        className="border-gray-300"
                      />
                    </div>
                  )}

                  {isFieldVisible('facebook') && (
                    <div className="space-y-2">
                      <Label className="text-sm text-gray-700">Facebook</Label>
                      <Input
                        value={signatureData.facebook}
                        onChange={(e) => handleDataChange('facebook', e.target.value)}
                        disabled={isFieldLocked('facebook')}
                        placeholder="username"
                        className="border-gray-300"
                      />
                    </div>
                  )}

                      {isFieldVisible('instagram') && (
                        <div className="space-y-2">
                          <Label className="text-sm text-gray-700">Instagram</Label>
                          <Input
                            value={signatureData.instagram}
                            onChange={(e) => handleDataChange('instagram', e.target.value)}
                            disabled={isFieldLocked('instagram')}
                            placeholder="username"
                            className="border-gray-300"
                          />
                        </div>
                      )}

                      {isFieldVisible('youtube') && (
                        <div className="space-y-2">
                          <Label className="text-sm text-gray-700">YouTube</Label>
                          <Input
                            value={signatureData.youtube}
                            onChange={(e) => handleDataChange('youtube', e.target.value)}
                            disabled={isFieldLocked('youtube')}
                            placeholder="channel"
                            className="border-gray-300"
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Only show Images section if profile image is visible */}
                {isFieldVisible('profileImage') && (
                  <>
                    <h3 className="text-lg font-semibold text-gray-900 pt-4">Images</h3>
                    <div className="space-y-4">
                      {/* Profile Picture */}
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                          {signatureData.profileImage ? (
                            <img src={signatureData.profileImage} alt="Profile" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">No image</div>
                          )}
                        </div>
                        <div className="flex-1">
                          <Label className="text-sm text-gray-700 mb-2 block">Profile Picture</Label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, 'profile')}
                            className="hidden"
                            id="profile-upload"
                          />
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => document.getElementById('profile-upload').click()}
                            >
                              <Upload className="h-3 w-3 mr-2" />
                              Upload
                            </Button>
                            {signatureData.profileImage && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDataChange('profileImage', null)}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Brand Color - Only show if visible */}
                {isFieldVisible('brandColor') && (
                  <div className="space-y-2">
                    <Label className="text-sm text-gray-700">Brand Color</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="color"
                        value={signatureData.brandColor}
                        onChange={(e) => handleDataChange('brandColor', e.target.value)}
                        disabled={isFieldLocked('brandColor')}
                        className="w-16 h-10 p-1 cursor-pointer border-gray-300"
                      />
                      <Input
                        value={signatureData.brandColor}
                        onChange={(e) => handleDataChange('brandColor', e.target.value)}
                        disabled={isFieldLocked('brandColor')}
                        className="flex-1 border-gray-300"
                      />
                    </div>
                    <div className="flex items-center space-x-2 mt-2">
                      <Switch
                        checked={signatureData.useBrandColorForSocial}
                        onCheckedChange={(checked) => handleDataChange('useBrandColorForSocial', checked)}
                        disabled={isFieldLocked('brandColor')}
                      />
                      <Label className="text-xs text-gray-600">Use brand color for social icons</Label>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Preview */}
          <div className="space-y-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h2 className="text-xl font-semibold text-gray-900">Email Size</h2>
                    <div className="text-sm">
                      <span className={`font-semibold ${
                        charCount > 10000 ? 'text-red-600' : 
                        charCount > 8000 ? 'text-orange-500' : 
                        'text-green-600'
                      }`}>
                        {charCount.toLocaleString()}
                      </span>
                      <span className="text-gray-500"> / 10,000 characters</span>
                    </div>
                  </div>
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="h-2.5 rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.min((charCount / 10000) * 100, 100)}%`,
                        backgroundColor: 
                          charCount > 10000 ? '#dc2626' : 
                          charCount > 8000 ? '#f59e0b' : 
                          '#10b981',
                      }}
                    ></div>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {charCount > 10000 && (
                      <span className="text-red-600 font-medium">⚠️ Over Gmail/Outlook limit</span>
                    )}
                    {charCount > 8000 && charCount <= 10000 && (
                      <span className="text-orange-600">⚠️ Approaching limit</span>
                    )}
                    {charCount <= 8000 && (
                      <span className="text-green-600">✓ Good size for all email clients</span>
                    )}
                  </div>
                </div>

                {/* Preview Container */}
                <div className="border border-gray-200 rounded-lg p-6 bg-gray-50 mb-6 min-h-[400px]">
                  {/* Responsive wrapper for mobile scaling */}
                  <div className="overflow-x-auto">
                    <div 
                      ref={signatureRef}
                      className="signature-preview-wrapper"
                    >
                      <SignaturePreview
                        signatureData={signatureData}
                        visibilitySettings={fieldVisibility}
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button
                    onClick={handleCopy}
                    className="w-full"
                    size="lg"
                    style={{ backgroundColor: signatureData.brandColor }}
                  >
                    {copied ? (
                      <>
                        <Check className="mr-2 h-5 w-5" />
                        Copied to Clipboard!
                      </>
                    ) : (
                      <>
                        <Copy className="mr-2 h-5 w-5" />
                        Copy Signature
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={handleDownloadVCard}
                    variant="outline"
                    className="w-full"
                    size="lg"
                  >
                    <Download className="mr-2 h-5 w-5" />
                    Download vCard
                  </Button>
                </div>

                {/* Instructions */}
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <h3 className="text-sm font-semibold text-blue-900 mb-2">How to Install</h3>
                  <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                    <li>Click "Copy Signature" above</li>
                    <li>Open your email client settings</li>
                    <li>Navigate to the signature section</li>
                    <li>Paste your signature (Ctrl+V or Cmd+V)</li>
                    <li>Save your settings</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        )}
      </div>

      {/* Image Cropper Modal */}
      {imageToCrop && (
        <ImageCropper
          open={cropperOpen}
          onClose={() => {
            setCropperOpen(false);
            setImageToCrop(null);
          }}
          imageSrc={imageToCrop.src}
          onCropComplete={handleCropComplete}
          aspectRatio={imageToCrop.aspectRatio}
          recommendedSize={imageToCrop.recommendedSize}
          allowFreeform={imageToCrop.allowFreeform || false}
          title={`Crop ${imageToCrop.type === 'profile' ? 'Profile Photo' : imageToCrop.type === 'banner' ? 'Banner' : 'Logo'}`}
        />
      )}
    </div>
  );
};

export default SignatureTool;
